/**
 * 이벤트 화이트리스트 (Phase 1 — 온톨로지 V0).
 *
 * "사용자 모든 행동 로그" 라고 해도 실제 수집은 명시적으로 정의된 이벤트만.
 * Why 화이트리스트:
 *   - 실수로 비밀번호 입력 필드 클릭이 click event 로 전송되는 것 방지
 *   - PII 가 label 에 들어가는 것 방지 (다단 방어)
 *   - admin 분석 시 "수집 항목 명세" 가 항상 명확
 *
 * Phase 2 에서 도메인 이벤트 (analyze.viewed, trade.executed 등) 가 추가될 때
 * 여기에 등록만 하면 자동 통과.
 */

/** 자동 추적되는 raw 이벤트 type */
export const ALLOWED_EVENT_TYPES = new Set([
  "pageview",
  "dwell",
  "click",
  "custom",
]);

/**
 * 클릭 라벨 화이트리스트 (kebab-case, prefix 권장).
 * Phase 2 에서 도메인 라벨 추가 예정 (analyze:start, compare:add 등).
 *
 * 빈 배열 = 모든 라벨 허용 (현재 V0 — 일단 다 받고 PII redaction 으로 안전 확보).
 * 강력한 제한이 필요하면 ALLOWED_CLICK_LABELS 에 prefix 등록.
 */
export const ALLOWED_CLICK_LABEL_PREFIXES: string[] = [
  // 빈 배열 = 모두 허용 (V0)
  // 예: "analyze:", "compare:", "report:" 등
];

/**
 * 라벨이 화이트리스트 통과하는지.
 * prefix 가 비어있으면 모두 허용.
 */
export function isAllowedClickLabel(label: string): boolean {
  if (ALLOWED_CLICK_LABEL_PREFIXES.length === 0) return true;
  return ALLOWED_CLICK_LABEL_PREFIXES.some((p) => label.startsWith(p));
}

/**
 * custom 이벤트 라벨 — 명시 등록만 허용 (Phase 2 strict mode).
 * 새 custom 이벤트 추가 시 lib/analytics-events.ts 의 helper 추가 + 여기에 라벨 등록.
 *
 * 강제하는 이유:
 *   1) 오타 / 실수로 "analyze.viewd" 같은 라벨이 영구히 통계에 섞이는 것 방지
 *   2) 어드민 대시보드의 funnel/segment 빌더가 명세와 일관
 *   3) Phase 5 어드민 시각화에서 "수집 항목 표" 가 항상 정확
 */
export const ALLOWED_CUSTOM_LABELS = new Set<string>([
  // ANALYZE
  "analyze.viewed",
  "analyze.jobStarted",
  "analyze.reportRendered",
  "analyze.sectionExpanded",

  // COMPARE
  "compare.viewed",
  "compare.tickerAdded",
  "compare.tickerRemoved",

  // WATCHLIST
  "watchlist.added",
  "watchlist.removed",
  "watchlist.viewed",

  // CHAT
  "chat.messageSent",
  "chat.responseRendered",

  // ONTOLOGY
  "ontology.graphViewed",
  "ontology.relatedClicked",

  // TRADE
  "trade.executed",
  "trade.outcome1w",
  "trade.outcome1m",
  "trade.outcome1y",
  "trade.csvImported",
  "trade.kisConnected",
  "trade.kisSynced",

  // COMMUNITY
  "community.postCreated",
  "community.commentCreated",
  "community.postLiked",
  "community.postBookmarked",
  "community.leaderboardShown",
  "community.leaderboardClicked",

  // PAYMENT
  "payment.trialStarted",
  "payment.paid",
  "payment.downgraded",
  "payment.cancelled",

  // SUBSCRIBE
  "subscribe.tierViewed",
  "subscribe.planClicked",

  // RECOMMEND (V1 정확도 측정 baseline)
  "recommend.shown",
  "recommend.clicked",
  "recommend.dismissed",
]);

/** "open" = 모두 허용 (V0), "strict" = ALLOWED_CUSTOM_LABELS 만 허용 */
export const CUSTOM_LABEL_MODE: "open" | "strict" = "strict";

export function isAllowedCustomLabel(label: string): boolean {
  if (CUSTOM_LABEL_MODE === "open") return true;
  return ALLOWED_CUSTOM_LABELS.has(label);
}
